'use client';

import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';

interface ImageLightboxProps {
  src: string;
  alt: string;
  className?: string;
  thumbnailClassName?: string;
  preload?: boolean;
}

export default function ImageLightbox({
  src,
  alt,
  className = '',
  thumbnailClassName = '',
  preload = false,
}: ImageLightboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Ensure we only render portal on client
  useEffect(() => {
    setMounted(true);
  }, []);

  // Handle escape key to close
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, []);

  // Add/remove event listener and body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Modal content
  const modalContent = isOpen ? (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 99999 }}
      onClick={() => setIsOpen(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/95 backdrop-blur-sm" />

      {/* Close button */}
      <button
        className="absolute top-6 right-6 z-10 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors border border-white/20"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(false);
        }}
        aria-label="Close lightbox"
      >
        <svg
          className="w-8 h-8 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* Image title */}
      <div className="absolute top-6 left-6 text-white/80 text-lg font-medium max-w-[60%] truncate">
        {alt}
      </div>

      {/* Help text */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-sm bg-black/50 px-4 py-2 rounded-full">
        Press ESC or click anywhere to close
      </div>

      {/* Full-size image container */}
      <div
        className="relative w-[95vw] h-[85vh] max-w-7xl"
        onClick={(e) => e.stopPropagation()}
      >
        <Image
          src={src}
          alt={alt}
          fill
          className="object-contain"
          loading="eager"
          sizes="95vw"
          quality={100}
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      {/* Thumbnail - Clickable */}
      <button
        type="button"
        className={`relative cursor-pointer group ${className}`}
        onClick={() => setIsOpen(true)}
        aria-label={`View ${alt} full size`}
      >
        <Image
          src={src}
          alt={alt}
          fill
          className={`object-contain transition-transform duration-300 group-hover:scale-105 ${thumbnailClassName}`}
          preload={preload}
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 600px"
        />
        {/* Hover overlay with zoom icon */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 rounded-full p-3">
            <svg
              className="w-6 h-6 text-neutral-800"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
              />
            </svg>
          </div>
        </div>
      </button>

      {/* Render modal using portal to escape z-index stacking context */}
      {mounted && modalContent && createPortal(modalContent, document.body)}
    </>
  );
}
